import { useState, type Dispatch, type SetStateAction } from "react";
import { Box } from "@mui/material";
import { t } from "i18next";
import { useTranslation } from "react-i18next";

import type {
  PolicePresenceRequested,
  ShareableIncidentDataV1Response,
} from "@thalamos/common";
import type {
  FormContextData,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";

import { ArePolicePresentDialog } from "./ArePolicePresentDialog.js";
import { RecordPolicePresenceDialog } from "./RecordPolicePresenceDialog.js";
import { RequestPolicePresentDialog } from "./RequestPolicePresenceDialog.js";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import { PatientBanner } from "~/components/layout/index.js";
import { MultiPagePdf } from "~/components/MultiPagePdf.js";

export type RevewPoliceFormAndRequestPolicePresenceV2PageState =
  | "initial"
  | "arePolicePresent"
  | "requestPolicePresence"
  | "recordPolicePresence";

export type RequestAndRecordPolicePresenceV2ModalProps = {
  officerRequest: ShareableIncidentDataV1Response;
  formContext: FormContextData;
  reloadFormContext: () => void;
  reloadShareableIncidentData: () => void;
  pageState: RevewPoliceFormAndRequestPolicePresenceV2PageState;
  setPageState: Dispatch<
    SetStateAction<RevewPoliceFormAndRequestPolicePresenceV2PageState>
  >;
  onFlowComplete?: () => void;
};

export function RequestAndRecordPolicePresenceV2Modals({
  officerRequest,
  formContext,
  reloadFormContext,
  reloadShareableIncidentData,
  pageState,
  setPageState,
  onFlowComplete,
}: RequestAndRecordPolicePresenceV2ModalProps) {
  const { t } = useTranslation();

  const [policePresenceRequestData, setPolicePresenceRequestData] = useState<
    PolicePresenceRequested["policePresenceRequestData"] | null
  >(null);

  const [arePolicePresentResponse, setArePolicePresentResponse] = useState<
    "yes" | "no" | null
  >(null);

  const dontRequestPolicePresence = async (reason: "no" | "already-left") => {
    setIsSubmitting(true);
    try {
      const result = await api.vision.requestPolicePresenceV2(formContext.id, {
        type: "recordPolicePresence",
        data: {
          type: "notRequestPolicePresence",
          policePresenceRequestData: {
            hasPolicePresenceRequested: {
              valueLabel:
                reason === "no"
                  ? t(
                      "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.hasPolicePresenceBeenRequestedNo",
                    )
                  : t(
                      "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.hasPolicePresenceBeenRequestedAlreadyLeft",
                    ),
              value: reason,
            },
          },
        },
      });
      if (result.status === 204) {
        setIsSubmitting(false);
        reloadFormContext();
        reloadShareableIncidentData();
        setPageState("initial");
        renderSuccessToast({
          message: t(
            "pages.policeIncidentFormPage.toastNotifications.policePresenceNotRequested",
          ),
        });
        onFlowComplete?.();
      } else {
        // @ts-expect-error - data is "unknown"
        const errorMessage = result.data?.reason ?? "Unknown error";
        renderErrorToast({ message: errorMessage });
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error requesting police presence:", error);
      renderErrorToast({
        message: t(
          "pages.policeIncidentFormPage.toastNotifications.requestPolicePresenceFailed",
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        {pageState === "arePolicePresent" && (
          <ArePolicePresentDialog
            isDisabled={isSubmitting}
            onCloseOrOnTheirWay={() => {
              setArePolicePresentResponse(null);
              setPageState("initial");
            }}
            onYes={() => {
              setPageState("requestPolicePresence");
            }}
            onPreviouslyPresent={async () => {
              await dontRequestPolicePresence("already-left");
            }}
            officerRequest={officerRequest}
            version={2}
            arePolicePresentResponse={arePolicePresentResponse}
          />
        )}

        {pageState === "requestPolicePresence" && (
          <RequestPolicePresentDialog
            officerName={officerRequest.officerDetails.name}
            isDisabled={isSubmitting}
            version={2}
            onClose={() => {
              setPolicePresenceRequestData(null);
              setArePolicePresentResponse(null);
              setPageState("initial");
            }}
            onGoBack={() => {
              setPolicePresenceRequestData(null);
              setArePolicePresentResponse("yes");
              setPageState("arePolicePresent");
            }}
            policePresenceRequestData={policePresenceRequestData || undefined}
            onRequestPresence={({
              reasonLabel,
              reasonValue,
              furtherReasoning,
            }) => {
              // Store the data and move to record police presence modal
              setPolicePresenceRequestData({
                hasPolicePresenceRequested: {
                  valueLabel: "Yes",
                  value: "yes",
                },
                policePresenceRequestedReason: {
                  valueLabel: reasonLabel,
                  value: reasonValue,
                },
                furtherReasoning: { value: furtherReasoning },
              });

              setPageState("recordPolicePresence");
            }}
            onDontRequestPresence={async () => {
              await dontRequestPolicePresence("no");
            }}
          />
        )}

        {pageState === "recordPolicePresence" && policePresenceRequestData && (
          <RecordPolicePresenceDialog
            isDisabled={isSubmitting}
            onClose={() => {
              setPolicePresenceRequestData(null);
              setArePolicePresentResponse(null);
              setPageState("initial");
            }}
            onGoBack={() => {
              setPageState("requestPolicePresence");
            }}
            onSubmit={(recordPolicePresenceData) => {
              const onRequestPolicePresence = async () => {
                setIsSubmitting(true);
                try {
                  const result = await api.vision.requestPolicePresenceV2(
                    formContext.id,
                    {
                      type: "recordPolicePresence",
                      data: {
                        type: "requestPolicePresence",
                        policePresenceRequestData,
                        recordPolicePresenceData,
                      },
                    },
                  );
                  if (result.status === 204) {
                    setIsSubmitting(false);
                    reloadFormContext();
                    reloadShareableIncidentData();
                    setPageState("initial");
                    renderSuccessToast({
                      message: t(
                        "pages.policeIncidentFormPage.toastNotifications.policePresenceInformed",
                      ),
                    });
                    onFlowComplete?.();
                  } else {
                    // @ts-expect-error - data is "unknown"
                    const errorMessage = result.data?.reason ?? "Unknown error";
                    renderErrorToast({ message: errorMessage });
                    setIsSubmitting(false);
                  }
                } catch (error) {
                  console.error("Error recording police presence:", error);
                  renderErrorToast({
                    message: t(
                      "pages.policeIncidentFormPage.toastNotifications.requestPolicePresenceFailed",
                    ),
                  });
                } finally {
                  setIsSubmitting(false);
                }
              };
              void onRequestPolicePresence();
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export function ReviewPoliceFormAndRequestPresencePageV2({
  patientTimeline,
  reloadPatientTimeline,
  officerRequest,
  formContext,
  reloadFormContext,
  reloadShareableIncidentData,
  onFlowComplete,
  onReconcileClick,
}: Omit<
  RequestAndRecordPolicePresenceV2ModalProps,
  "pageState" | "setPageState"
> & {
  patientTimeline: PatientTimelineResponse;
  reloadPatientTimeline: () => void;
  /**
   * When provided and the patient's NHS number is unknown/missing, the NHS number
   * in the PatientBanner becomes clickable to open the reconciliation flow.
   * Has no effect when the patient already has a valid NHS number.
   */
  onReconcileClick?: () => void;
}) {
  const [pageState, setPageState] =
    useState<RevewPoliceFormAndRequestPolicePresenceV2PageState>("initial");
  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <PatientBanner
          patient={patientTimeline.patient}
          nhsNumber={patientTimeline.patient.nhsNumber ?? undefined}
          isHorizontalLineHidden
          patientTimeline={patientTimeline}
          reloadPatientTimeline={reloadPatientTimeline}
          onReconcileClick={onReconcileClick}
        />
      </Box>
      <Banner
        bannerType={BannerList.WARNING}
        body={[
          t("pages.policeIncidentFormPage.formAccessNotice.invitationMessage", {
            name: officerRequest.officerDetails.name,
          }),
        ]}
      />
      <MultiPagePdf data={officerRequest.incidentPdfBase64Data} />
      <RequestAndRecordPolicePresenceV2Modals
        officerRequest={officerRequest}
        pageState={pageState}
        setPageState={setPageState}
        formContext={formContext}
        reloadFormContext={reloadFormContext}
        reloadShareableIncidentData={reloadShareableIncidentData}
        onFlowComplete={onFlowComplete}
      />
      <FormFooterSection
        isSticky={true}
        isForcedSticky={true}
        saveLabel={t("buttonLabels.continueToDownload")}
        hideDiscard
        onSave={async () => setPageState("arePolicePresent")}
        onCancel={() => {}}
      />
    </Box>
  );
}
